import type {
  TApiApplication,
  TApiRequiredDocument,
  TApplicationStatus,
  TDocumentStatus,
  TRequireDocumentStatus,
} from '~/modules/covenants/types.ts';
import { StatusFilterValue } from '~/modules/covenants/types.ts';

export type TStatusColor = 'success' | 'default' | 'warning' | 'error' | 'info' | 'primary';

export interface IStatusConfig {
  label: string;
  color: TStatusColor;
  description: string;
}

export const APP_STATUS_MAP: Record<TApplicationStatus, IStatusConfig> = {
  [StatusFilterValue.New]: {
    label: 'Створено',
    color: 'default',
    description:
      'Заявку створено, але ще не відправлено до банку. Ви можете додавати або змінювати документи.',
  },
  [StatusFilterValue.Sent]: {
    label: 'Відправлено',
    color: 'default',
    description: 'Заявку відправлено до банку. Вона очікує або проходить перевірку.',
  },
  [StatusFilterValue.Accepted]: {
    label: 'Погоджено',
    color: 'success',
    description: 'Заявку погоджено банком. Додаткових дій за нею не потрібно.',
  },
  [StatusFilterValue.Denied]: {
    label: 'Відхилено',
    color: 'error',
    description:
      'Заявку відхилено банком. Потрібно врахувати зауваження та подати документи повторно.',
  },
  [StatusFilterValue.PartlyAccepted]: {
    label: 'Доопрацювання',
    color: 'warning',
    description: 'Частину документів погоджено, а частину потрібно виправити та подати повторно.',
  },
};

export const APPLICATION_DOCUMENT_STATUS_MAP: Record<TDocumentStatus, IStatusConfig> = {
  NEW: {
    label: 'Новий',
    color: 'default',
    description: 'Документ додано до заявки, але ще не відправлено до банку.',
  },
  SENT: {
    label: 'Обробка',
    color: 'default',
    description: 'Документ відправлено до банку та він перебуває на розгляді.',
  },
  DENIED: {
    label: 'Відхилено',
    color: 'error',
    description:
      'Документ відхилено. Перегляньте коментар до документа та завантажте оновлений файл.',
  },
  ACCEPTED: {
    label: 'Прийнято',
    color: 'success',
    description: 'Документ перевірено та прийнято банком.',
  },
};

export const REQUIRED_DOCUMENT_STATUS_MAP: Record<TRequireDocumentStatus, IStatusConfig> = {
  PENDING: {
    label: 'Очікує документ',
    color: 'error',
    description: 'Банк очікує цей документ. Його потрібно додати до заявки.',
  },
  IN_PROGRESS: {
    label: 'Обробка',
    color: 'default',
    description: 'Документ уже завантажено та передано на подальшу обробку.',
  },
  ACCEPTED: {
    label: 'Прийнято',
    color: 'success',
    description: 'Документ за цією вимогою вже прийнято.',
  },
};

export const MANAGER_APP_STATUS_MAP: Record<TApplicationStatus, IStatusConfig> = {
  [StatusFilterValue.New]: {
    ...APP_STATUS_MAP[StatusFilterValue.New],
    label: 'Нова',
  },
  [StatusFilterValue.Sent]: {
    ...APP_STATUS_MAP[StatusFilterValue.Sent],
    label: 'Передано в Банк',
  },
  [StatusFilterValue.Accepted]: {
    ...APP_STATUS_MAP[StatusFilterValue.Accepted],
    label: 'Прийнята',
  },
  [StatusFilterValue.Denied]: {
    ...APP_STATUS_MAP[StatusFilterValue.Denied],
  },
  [StatusFilterValue.PartlyAccepted]: {
    ...APP_STATUS_MAP[StatusFilterValue.PartlyAccepted],
    label: 'Частково прийнята',
  },
};

export const MANAGER_APPLICATION_DOCUMENT_STATUS_MAP: Partial<
  Record<TApplicationStatus, IStatusConfig>
> = {
  [StatusFilterValue.Sent]: {
    label: 'Передано в Банк',
    color: 'default',
    description: 'Документ передано в банк та він очікує перевірку.',
  },
  [StatusFilterValue.Denied]: {
    label: 'Відхилено',
    color: 'error',
    description: 'Документ відхилено. Перегляньте коментар та підготуйте оновлену версію.',
  },
  [StatusFilterValue.Accepted]: {
    label: 'Прийнято',
    color: 'success',
    description: 'Документ перевірено та прийнято банком.',
  },
};

const DEFAULT_STATUS_CONFIG = (status: string): IStatusConfig => ({
  label: status,
  color: 'default',
  description: 'Опис для цього статусу ще не налаштовано.',
});

export const getAppStatusConfig = (status: TApiApplication['status']): IStatusConfig =>
  APP_STATUS_MAP[status] ?? DEFAULT_STATUS_CONFIG(status);

export const getApplicationDocumentStatusConfig = (status: TDocumentStatus): IStatusConfig =>
  APPLICATION_DOCUMENT_STATUS_MAP[status] ?? DEFAULT_STATUS_CONFIG(status);

export const getRequiredDocumentStatusConfig = (
  status: TApiRequiredDocument['status']
): IStatusConfig => REQUIRED_DOCUMENT_STATUS_MAP[status] ?? DEFAULT_STATUS_CONFIG(status);

export const getManagerAppStatusConfig = (status: TApplicationStatus): IStatusConfig =>
  MANAGER_APP_STATUS_MAP[status] ?? DEFAULT_STATUS_CONFIG(status);

export const getManagerApplicationDocumentStatusConfig = (
  status: TApplicationStatus
): IStatusConfig =>
  MANAGER_APPLICATION_DOCUMENT_STATUS_MAP[status] ?? getManagerAppStatusConfig(status);
